import type { ContentBlock, SiteSettings } from '@sazerac/design-tokens';
import { HeroBlock } from './HeroBlock';
import { SplitFeatureBlock } from './SplitFeatureBlock';
import { DayNightBlock } from './DayNightBlock';
import { SignatureCarouselBlock } from './SignatureCarouselBlock';
import { MenuSplitBlock } from './MenuSplitBlock';
import { ImageBannerBlock } from './ImageBannerBlock';
import { ExperienceGridBlock } from './ExperienceGridBlock';
import { ServicesDuoBlock } from './ServicesDuoBlock';
import { TestimonialsBlock } from './TestimonialsBlock';
import { GalleryStripBlock, InstagramGridBlock } from './GalleryBlocks';
import { VisitUsBlock } from './VisitUsBlock';
import { NewsletterBlock } from './NewsletterBlock';
import { PageHeroBlock, StorySectionsBlock, RichTextBlock, FaqListBlock } from './PageBlocks';

export interface BlockContext {
  index: number;
  site?: SiteSettings;
}

/**
 * Maps a CMS block type to its React component.
 *
 * This is where the design-runtime placeholders from the export (`image-slot`, `sc-if`,
 * `sc-for`, `x-dc`) become real components backed by API data. An unknown block type is
 * skipped rather than crashing the page, so adding a block type to the CMS can never take
 * the live site down.
 */
export function renderBlock(block: ContentBlock, context: BlockContext) {
  const data = block.data as Record<string, never>;
  const key = block.id || `${block.type}-${context.index}`;

  switch (block.type) {
    case 'hero':
      return <HeroBlock key={key} data={data} />;
    case 'splitFeature':
      return <SplitFeatureBlock key={key} data={data} />;
    case 'dayNight':
      return <DayNightBlock key={key} data={data} />;
    case 'signatureCarousel':
      return <SignatureCarouselBlock key={key} data={data} />;
    case 'menuSplit':
      return <MenuSplitBlock key={key} data={data} />;
    case 'imageBanner':
      return <ImageBannerBlock key={key} data={data} />;
    case 'experienceGrid':
      return <ExperienceGridBlock key={key} data={data} />;
    case 'servicesDuo':
      return <ServicesDuoBlock key={key} data={data} />;
    case 'testimonials':
      return <TestimonialsBlock key={key} data={data} />;
    case 'galleryStrip':
      return <GalleryStripBlock key={key} data={data} />;
    case 'instagramGrid':
      return <InstagramGridBlock key={key} data={data} />;
    case 'visitUs':
      return <VisitUsBlock key={key} data={data} site={context.site} />;
    case 'newsletter':
      return <NewsletterBlock key={key} data={data} />;
    case 'pageHero':
      return <PageHeroBlock key={key} data={data} />;
    case 'storySections':
      return <StorySectionsBlock key={key} data={data} />;
    case 'richText':
      return <RichTextBlock key={key} data={data} />;
    case 'faqList':
      return <FaqListBlock key={key} data={data} />;
    default:
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`Unknown content block type "${block.type}" — skipped.`);
      }
      return null;
  }
}

/** Blocks reference media by id; the API returns the resolved URL on the page payload. */
export interface MediaRef {
  mediaId?: string | null;
}

export interface CtaData {
  label?: string;
  href?: string;
}
